import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useBrandName } from '@jotta/grpc-connect-client/config'
import {
  useCheckPasswordReset,
  useResetPasswordMutation,
} from '@jotta/grpc-connect-client/login'
import { useForm } from '@jotta/hooks'
import { BaseErrorPage } from '@jotta/ui/BaseErrorPage'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Form } from '@jotta/ui/Form'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import SvgCheck from '@jotta/ui/SvgCheck'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { CTAButton } from '../../auth/CTAButton'
import { PasswordField } from '../../auth/PasswordField'
import { usePasswordField } from '../../auth/signupForm'
import { EntryLayout } from '../../components/Layout/EntryLayout'

export function SetPasswordPage({
  title,
  resetKey,
}: {
  title: ReactNode
  resetKey: string
}) {
  const { mutateAsync: resetPassword, isSuccess } = useResetPasswordMutation()

  const form = useForm({
    fields: {
      password: usePasswordField({
        focusOnMount: true,
      }),
    },
    submit: ({ password }) => resetPassword({ resetKey, password }),
  })

  const { password, onSubmit, isSubmitting } = form

  const heading = isSuccess ? t`Your password has been updated` : title
  const element = isSuccess ? (
    <>
      <div className="alert alert-good flex gap-4">
        <PlainBrandIcon icon={SvgCheck} height={24} />
        <Trans>
          Your password has been changed. Please login with your new password.
        </Trans>
      </div>
      <a className="btn btn-primary mt-4" href="/api/logout">
        <Trans>Log in</Trans>
      </a>
    </>
  ) : (
    <Form form={form} onSubmit={onSubmit}>
      <PasswordField field={password} />
      <CTAButton loading={isSubmitting} type="submit" className="w-full">
        <Trans>Save</Trans>
      </CTAButton>
    </Form>
  )

  return (
    <EntryLayout>
      <h1 className="title-md">{heading}</h1>
      <div className="mt-8 w-full">{element}</div>
    </EntryLayout>
  )
}

export function SetPasswordError({ title }: { title: ReactNode }) {
  return (
    <EntryLayout>
      <BaseErrorPage title={title} reportError={undefined}>
        <p>
          <Trans>The set password URL is invalid or already used.</Trans>
        </p>
        <p>
          <a href="/" className="!text-signup-link underline">
            <Trans>Click here to go back to the homepage.</Trans>
          </a>
        </p>
      </BaseErrorPage>
    </EntryLayout>
  )
}

export function SetPasswordRoute({ type = 'set' }: { type?: 'set' | 'reset' }) {
  const { resetKey = '' } = useParams()
  const { data } = useCheckPasswordReset(resetKey)
  const brand = useBrandName()
  const title =
    type === 'set' ? (
      <Trans>Choose a password for your {brand} account</Trans>
    ) : (
      t`Set your new password`
    )

  if (!data) {
    return <LoadingOverlay id="SetPasswordRoute" open />
  }

  switch (data.result.case) {
    case 'invalid':
      return <SetPasswordError title={title} />

    case 'redirect':
      window.location.href = data.result.value.url
      return <LoadingOverlay id="SetPasswordRoute" open />

    case 'valid':
      return <SetPasswordPage title={title} resetKey={resetKey} />

    case undefined:
      throw new Error('Invalid response')

    default:
      return exhaustiveGuard(data.result)
  }
}
